import type { PublishedLocaleManifest } from '@/i18n/types';

const staticPageContentCache = new Map<string, Promise<unknown | null>>();

export async function loadStaticPageSection<T>(
    manifest: PublishedLocaleManifest,
    locale: string,
    pageSlug: string,
    sectionKey: string,
): Promise<T | null> {
    if (!isPublishedSection(manifest, pageSlug, sectionKey)) {
        return null;
    }

    const resolvedLocale = manifest.locales.some(item => item.code === locale) ? locale : manifest.defaultLocale;
    const cacheKey = `${manifest.snapshotVersion}:${resolvedLocale}:${pageSlug}:${sectionKey}`;

    if (!staticPageContentCache.has(cacheKey)) {
        staticPageContentCache.set(cacheKey, loadPublishedPageSection(manifest, resolvedLocale, pageSlug, sectionKey));
    }

    return (await staticPageContentCache.get(cacheKey)) as T | null;
}

export function resetStaticPageContentCache(): void {
    staticPageContentCache.clear();
}

function isPublishedSection(manifest: PublishedLocaleManifest, pageSlug: string, sectionKey: string): boolean {
    return manifest.editorialPages[pageSlug]?.includes(sectionKey) ?? false;
}

async function loadPublishedPageSection<T>(
    manifest: PublishedLocaleManifest,
    locale: string,
    pageSlug: string,
    sectionKey: string,
): Promise<T | null> {
    const primaryResponse = await fetchPageSection<T>(manifest.snapshotVersion, locale, pageSlug, sectionKey);

    if (primaryResponse !== null) {
        return primaryResponse;
    }

    if (locale === manifest.defaultLocale) {
        return null;
    }

    return fetchPageSection<T>(manifest.snapshotVersion, manifest.defaultLocale, pageSlug, sectionKey);
}

async function fetchPageSection<T>(
    snapshotVersion: string,
    locale: string,
    pageSlug: string,
    sectionKey: string,
): Promise<T | null> {
    try {
        const response = await fetch(
            `/page-content/${locale}/${pageSlug}/${sectionKey}.json?v=${encodeURIComponent(snapshotVersion)}`,
        );

        if (!response.ok) {
            return null;
        }

        return (await response.json()) as T;
    } catch {
        return null;
    }
}
